/*
  The contract editor's logic, shared by desktop's EditContractInfo and the phone and tablet page
  (EditContractPagePhone): the queries it loads, its defaults, checks and change rules, property
  selection, and the save (contract, activity log, invoice, invoice email) in desktop's order.
*/
import {
  ActivityLog,
  Contract,
  Document,
  Event,
  Invoice,
  type Property,
  SQSEmail,
  SQSEmailAndDocument,
} from '@kalos/kalos-rpc';
import { useQuery } from '@tanstack/react-query';
import { addYears, format, isBefore, parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { queryKeys } from '../../../hooks/react-query/constants';
import { useContractFrequencyBatchGetQuery } from '../../../hooks/react-query/useContractFrequencyQuery';
import { useContractQuery } from '../../../hooks/react-query/useContractsQuery';
import { useInvoiceQuery } from '../../../hooks/react-query/useInvoiceQuery';
import { usePropertiesBatchGetQuery } from '../../../hooks/react-query/usePropertiesQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import {
  ActivityLogClientService,
  ContractClientService,
  InvoiceClientService,
  timestamp,
} from '../../../tools/helpers';
import { type ContractErrors } from './reducer';

/** Max length for multiline contract text fields (notes, payment terms). */
export const CONTRACT_TEXT_MAX_LENGTH = 500;

export const checkContractErrors = (
  data: Contract,
  fields?: (keyof Contract)[],
): ContractErrors => {
  const errors: ContractErrors = {};
  if (!fields || fields?.includes('dateEnded') || fields?.includes('dateStarted')) {
    if (isBefore(new Date(data.dateEnded), new Date(data.dateStarted))) {
      errors.dateStarted = 'Start Date cannot be earlier than End Date';
    }
  }
  if ((!fields || fields?.includes('paymentType')) && !data.paymentType) {
    errors.paymentType = 'This field is required';
  }
  if ((!fields || fields?.includes('departmentId')) && !data.departmentId) {
    errors.departmentId = 'This field is required';
  }
  if (!data.paymentStatus && fields?.includes('paymentStatus')) {
    errors.paymentStatus = 'This field is required';
  }
  if (data.notes.length > CONTRACT_TEXT_MAX_LENGTH) {
    errors.notes = `This field cannot exceed ${CONTRACT_TEXT_MAX_LENGTH} characters.`;
  }
  if (data.paymentTerms.length > CONTRACT_TEXT_MAX_LENGTH) {
    errors.paymentTerms = `This field cannot exceed ${CONTRACT_TEXT_MAX_LENGTH} characters.`;
  }
  return errors;
};

/** The checks desktop runs on a change: the fields that differ from the pending save, plus any already failing. */
export const checkContractChange = (
  contractData: Contract,
  pendingSave: Contract,
  currentErrors: ContractErrors,
): ContractErrors => {
  const fields: (keyof Contract)[] = Object.keys(contractData).filter(
    (key: string) =>
      contractData[key as keyof Contract] !== pendingSave[key as keyof Contract] ||
      Object.keys(currentErrors).includes(key),
  ) as (keyof Contract)[];
  return checkContractErrors(contractData, fields);
};

export const GROUP_BILLING_OPTIONS = [
  { name: 'Group', value: 1 },
  { name: 'Site', value: 0 },
] as const;

/** A new contract's starting values. */
export const buildNewContract = (userID: number) =>
  Contract.create({
    userId: userID,
    dateCreated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    dateStarted: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    dateEnded: format(addYears(new Date(), 1), 'yyyy-MM-dd HH:mm:ss'),
    timeStarted: '0001-01-01 00:00',
    timeEnded: '0001-01-01 00:00',
    frequency: 182,
    isActive: 1,
    paymentStatus: 'Pending',
  });

/** The contract fields the form edits, in desktop's order, with the type each control holds. */
export const CONTRACT_FORM_FIELDS = [
  { name: 'dateStarted', type: 'mui-date' },
  { name: 'dateEnded', type: 'mui-date' },
  { name: 'frequency', type: undefined },
  { name: 'groupBilling', type: undefined },
  { name: 'paymentType', type: undefined },
  { name: 'paymentStatus', type: undefined },
  { name: 'departmentId', type: 'department' },
  { name: 'timeStarted', type: 'mui-time' },
  { name: 'timeEnded', type: 'mui-time' },
  { name: 'notes', type: 'text' },
  { name: 'paymentTerms', type: 'text' },
] as const satisfies readonly { name: keyof Contract; type: string | undefined }[];

export type ContractFormField = (typeof CONTRACT_FORM_FIELDS)[number]['name'];

/** The form's starting values, as desktop's form takes them from the contract (an empty value takes its control's default). */
export const contractFormData = (contract: Contract): Contract => {
  const data: Record<string, unknown> = {};
  for (const { name, type } of CONTRACT_FORM_FIELDS) {
    const fallback =
      type === 'department' ? 0 : type === undefined && typeof contract[name] === 'number' ? 0 : '';
    data[name] = contract[name] || fallback;
  }
  return data as unknown as Contract;
};

/** Adds a changed field to a field mask as the server names it (`dateStarted` → `DateStarted`). */
export const addToFieldMask = (fieldMask: string[] | undefined, name: string) => {
  const masked = name.charAt(0).toUpperCase() + name.slice(1);
  const current = fieldMask ?? [];
  return current.includes(masked) ? current : current.concat(masked);
};

/** A department's default service window, set when the department changes. */
const DEPARTMENT_SERVICE_TIMES: Record<string, [string, string]> = {
  '19': ['0001-01-01 23:30', '0001-01-01 23:45'],
  '20': ['0001-01-01 23:30', '0001-01-01 23:45'],
  '17': ['0001-01-01 22:00', '0001-01-01 22:45'],
  '18': ['0001-01-01 23:00', '0001-01-01 23:45'],
};

/**
 * Desktop's change rules, giving the next pending save: always active and the customer's; a new
 * department sets its service window; a new start date moves the end date to a year after it.
 * Like desktop, it also writes those follow-on values back into the form data it's given.
 */
export const applyContractFormChange = (
  contractData: Contract,
  pendingSave: Contract,
  userID: number,
): Contract => {
  const req = Contract.clone(contractData);
  req.isActive = 1;
  req.userId = userID;
  if (req.fieldMask.includes('DepartmentId') && pendingSave.departmentId !== req.departmentId) {
    const times = DEPARTMENT_SERVICE_TIMES[String(req.departmentId)];
    if (times) {
      const [timeStarted, timeEnded] = times;
      req.timeStarted = timeStarted;
      req.timeEnded = timeEnded;
      contractData.timeStarted = timeStarted;
      contractData.timeEnded = timeEnded;
      req.fieldMask = req.fieldMask.concat('TimeStarted', 'TimeEnded');
    }
  }
  if (req.fieldMask.includes('DateStarted') && pendingSave.dateStarted !== req.dateStarted) {
    req.dateEnded = format(addYears(parseISO(req.dateStarted), 1), 'yyyy-MM-dd HH:mm:ss');
    contractData.dateEnded = format(addYears(parseISO(req.dateStarted), 1), 'yyyy-MM-dd HH:mm:ss');
    if (!req.fieldMask.includes('DateEnded')) {
      req.fieldMask = req.fieldMask.concat('DateEnded');
    }
  }
  return req;
};

/** The customer's properties with the contract's first (in the contract's order), and which are on it. */
export const orderContractProperties = (contractProperties: string, properties: Property[]) => {
  const sorted: Property[] = [];
  const selected: Property[] = [];
  contractProperties.split(',').forEach((result) => {
    properties
      .filter((property) => property.id === Number(result))
      .forEach((property) => {
        selected.push(property);
        sorted.push(property);
      });
  });
  properties.forEach((property) => {
    if (!sorted.includes(property)) sorted.push(property);
  });
  return { selected, sorted };
};

/** Adds the property to the selection, or takes it off (by id), without changing the original. */
export const toggleSelectedProperty = (selected: Property[] | undefined, property: Property) => {
  const next = selected ? [...selected] : [];
  const existingIndex = next.findIndex((prop) => prop.id === property.id);
  if (existingIndex >= 0) {
    next.splice(existingIndex, 1);
  } else {
    next.push(property);
  }
  return next;
};

export const filterPropertiesByAddress = (properties: Property[], search: string) =>
  properties.filter((prop) => prop.address.toLowerCase().includes(search.toLowerCase()));

/** Whether the saved contract covers the property (its PM count and PM job actions show only then). */
export const isPropertyOnContract = (contract: Contract, property: Property) =>
  !!contract.properties && contract.properties.split(',').includes(String(property.id));

/**
 * The PM job desktop makes for a property on the contract. `quick` is Quick create job (residential
 * by department); otherwise it's the job the new PM request opens with (never residential).
 */
export const buildContractPmEvent = (
  property: Property,
  contract: Contract,
  kind: 'quick' | 'request',
) => {
  let insert_description = `${property.address}, `;
  switch (property.zip) {
    case '34714': {
      insert_description = insert_description.concat(`S. Clermont`);
      break;
    }
    case '34711': {
      insert_description = insert_description.concat('Clermont');
      break;
    }
    case '34715': {
      insert_description = insert_description.concat('Clermont');
      break;
    }
    default: {
      insert_description = insert_description.concat(property.city);
    }
  }
  let event_description = '';
  event_description = event_description.concat(contract.notes);
  if (event_description === '') {
    event_description = 'PM';
  }
  const event = Event.create();
  event.name = `${insert_description} ${property.zip}`;
  event.description = 'PM';
  event.propertyId = property.id;
  event.contractId = contract.id;
  event.contractNumber = contract.number;
  event.dateStarted = `${format(new Date(), 'yyyy-MM-dd')} ${format(
    parseISO(contract.timeStarted),
    'HH:mm:ss',
  )}`;
  event.dateEnded = `${format(new Date(), 'yyyy-MM-dd')} ${format(
    parseISO(contract.timeEnded),
    'HH:mm:ss',
  )}`;
  event.timeStarted = format(parseISO(contract.timeStarted), 'HH:mm');
  event.timeEnded = format(parseISO(contract.timeEnded), 'HH:mm');
  event.color = 'fd9834';
  event.dateCreated = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
  event.dateUpdated = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
  event.logJobStatus = 'Pend Sched';
  event.jobTypeId = 3;
  event.logVersion = 1;
  event.logNotes = event_description;
  event.logPaymentType = contract.paymentType;
  event.logPaymentStatus = contract.paymentStatus;
  event.departmentId = contract.departmentId;
  if (kind === 'quick' && (contract.departmentId === 19 || contract.departmentId === 20)) {
    event.isResidential = 1;
  } else {
    event.isResidential = 0;
  }
  event.amountQuoted = contract.paymentTerms;
  return event;
};

/** The activity log line for an update: who changed what. Empty when nothing is in the field mask. */
export const buildContractActivityMessage = (
  entry: Contract,
  firstname: string | undefined,
  lastname: string | undefined,
) => {
  if (entry.fieldMask.length > 0) {
    let logMessage = `User ${firstname ?? ''} ${lastname ?? ''} changed the following \n`;
    for (let i = 0; i < entry.fieldMask.length; i++) {
      const field = entry.fieldMask[i] as keyof Contract;
      const messageString = `Changed ${field} to ${String(entry[field])} \n`;
      logMessage += messageString;
    }
    return logMessage;
  } else {
    return '';
  }
};

export const contractSaveErrorMessage = (error: string) =>
  `An error occurred while trying to save the contract: \n${error}.\n The contract or the created jobs have failed to save properly.  The invoice will not be saved. Please contact the webtech team if this issue persists.`;

/**
 * Saves the contract as desktop does: the pending save (changed in place) with the selected
 * properties; an update logs its changes, a create is approved and then numbered. Gives the saved
 * contract, or the message desktop shows when it fails.
 */
export const saveContractRecord = async ({
  pendingSave,
  propertiesSelected,
  contractID,
  currentUserId,
  activityMessage,
}: {
  pendingSave: Contract;
  propertiesSelected: Property[] | undefined;
  contractID: number;
  currentUserId: number;
  activityMessage: (entry: Contract) => string;
}): Promise<
  { contract: Contract; error?: undefined } | { contract?: undefined; error: string }
> => {
  let update = false;
  let contractRes: Contract | undefined;
  let error = '';
  const reqContract = pendingSave;
  if (contractID) {
    reqContract.id = contractID;
    update = true;
  }
  try {
    if (propertiesSelected !== undefined) {
      reqContract.properties = propertiesSelected.map((property) => `${property.id}`).join(',');
    } else {
      reqContract.properties = '';
    }
    if (!reqContract.fieldMask.includes('Properties')) {
      reqContract.fieldMask.push('Properties');
    }

    if (update) {
      contractRes = await ContractClientService.Update(reqContract);

      if (!contractRes) throw new Error('Contract failed to update.');

      const message = activityMessage(reqContract);
      if (message != '') {
        const activity = ActivityLog.create();
        activity.contractId = reqContract.id;
        activity.activityName = message;
        activity.activityDate = timestamp();
        activity.userId = currentUserId;
        await ActivityLogClientService.Create(activity);
      }
    } else {
      reqContract.dateCreated = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      reqContract.reviewStatus = 'approved';

      contractRes = await ContractClientService.Create(reqContract);

      if (!contractRes) throw new Error('Contract failed to create.');

      const contractNumber =
        String(contractRes.id).length < 5 ? `0${contractRes.id}` : `${contractRes.id}`;
      contractRes.number = `C${format(new Date(), 'yy')}-${contractNumber}`;
      await ContractClientService.Update(contractRes);
    }
  } catch (err) {
    console.error(`An error occurred while upserting a contract: ${err}`);
    error = `${err}`;
  }

  if (!contractRes || contractRes.id === 0 || error) {
    return { error: contractSaveErrorMessage(error) };
  }
  return { contract: contractRes };
};

export type PaymentProcessor = 'helcim' | 'stripe';

/**
 * Saves the contract's invoice as desktop does (creates it, or updates what changed), then, when
 * asked, sends the invoice email: to the customer when `sendInvoiceToCustomer`, else with no
 * recipient. Failures are logged, not thrown, as on desktop.
 */
export const saveContractInvoice = async ({
  contract,
  invoiceData,
  invoiceId,
  customerLoaded,
  customerEmail,
  sendInvoice,
  sendInvoiceToCustomer,
  paymentProcessor,
  userID,
  contractID,
}: {
  contract: Contract;
  invoiceData: Invoice;
  invoiceId: number;
  customerLoaded: boolean;
  customerEmail: string | undefined;
  sendInvoice: boolean;
  sendInvoiceToCustomer: boolean;
  paymentProcessor: PaymentProcessor;
  userID: number;
  contractID: number;
}) => {
  if (!customerLoaded) {
    console.error("Customer query isn't successful on invoice save, aborting.");
    return;
  }
  let _invoiceId = invoiceId;
  try {
    const reqInvoice = Invoice.clone(invoiceData);

    reqInvoice.contractId = contract.id;
    reqInvoice.logPaymentType = contract.paymentType;
    reqInvoice.logPaymentStatus = contract.paymentStatus;
    reqInvoice.servicesperformedrow1 = invoiceData.servicesperformedrow1;
    reqInvoice.totalamountrow1 = invoiceData.totalamountrow1;
    reqInvoice.servicesperformedrow2 = invoiceData.servicesperformedrow2;
    reqInvoice.totalamountrow2 = invoiceData.totalamountrow2;
    reqInvoice.servicesperformedrow3 = invoiceData.servicesperformedrow3;
    reqInvoice.totalamountrow3 = invoiceData.totalamountrow3;
    reqInvoice.servicesperformedrow4 = invoiceData.servicesperformedrow4;
    reqInvoice.totalamountrow4 = invoiceData.totalamountrow4;
    reqInvoice.totalamounttotal = `${invoiceData.totalamounttotal}`;
    reqInvoice.terms = invoiceData.terms;
    reqInvoice.userId = userID;

    if (_invoiceId === -1) {
      reqInvoice.startDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      const invoice = await InvoiceClientService.Create(reqInvoice);

      if (!invoice) throw new Error('Invoice failed to create.');

      _invoiceId = invoice.id;
    } else {
      reqInvoice.id = _invoiceId;
      const currentInv = await InvoiceClientService.Get(Invoice.create({ id: _invoiceId }));

      if (!currentInv) throw new Error('Invoice failed to load.');

      if (contract.paymentType !== currentInv.logPaymentType) {
        reqInvoice.fieldMask.push('LogPaymentType');
      }
      if (contract.paymentStatus !== currentInv.logPaymentStatus) {
        reqInvoice.fieldMask.push('LogPaymentStatus');
      }
      if (invoiceData.terms !== currentInv.terms) {
        reqInvoice.fieldMask.push('Terms');
      }
      const containsTotalamount = reqInvoice.fieldMask.some((field) =>
        field.includes('Totalamount'),
      );
      if (containsTotalamount) {
        reqInvoice.fieldMask.push('Totalamounttotal');
      }
      if (reqInvoice.fieldMask.length > 0) {
        await InvoiceClientService.Update(reqInvoice);
      }
    }
    if (sendInvoice) {
      const sqsInvoiceEmail = SQSEmailAndDocument.create();
      sqsInvoiceEmail.paymentType = paymentProcessor;
      const email = SQSEmail.create();
      const document = Document.create();
      document.invoiceId = _invoiceId;
      document.contractId = contractID;
      document.userId = userID;
      document.dateCreated = timestamp();
      email.to = '';
      if (sendInvoiceToCustomer === true) {
        email.to = customerEmail ?? '';
      }
      sqsInvoiceEmail.document = document;
      sqsInvoiceEmail.email = email;
      try {
        await InvoiceClientService.SendSQSInvoiceEmail(sqsInvoiceEmail);
      } catch (err) {
        console.error('we had an error updating the invoice', err);
      }
    }
  } catch (err) {
    console.error(`An error occurred while upserting an invoice: ${err}`);
  }
};

/** The invoice fields the form edits, in desktop's order. */
export const INVOICE_FORM_FIELDS = [
  { name: 'terms', label: 'Terms', type: 'text' },
  { name: 'servicesperformedrow1', label: 'Service Performed (1)', type: 'text' },
  { name: 'totalamountrow1', label: 'Total Cost (1)', type: 'number' },
  { name: 'servicesperformedrow2', label: 'Service Performed (2)', type: 'text' },
  { name: 'totalamountrow2', label: 'Total Cost (2)', type: 'number' },
  { name: 'servicesperformedrow3', label: 'Service Performed (3)', type: 'text' },
  { name: 'totalamountrow3', label: 'Total Cost (3)', type: 'number' },
  { name: 'servicesperformedrow4', label: 'Service Performed (4)', type: 'text' },
  { name: 'totalamountrow4', label: 'Total Cost (4)', type: 'number' },
] as const satisfies readonly { name: keyof Invoice; label: string; type: 'text' | 'number' }[];

export type InvoiceFormField = (typeof INVOICE_FORM_FIELDS)[number]['name'];

/** A typed amount as desktop's number field keeps it: a number when it parses, else the text. */
export const parseInvoiceAmount = (value: string): string | number =>
  isNaN(parseFloat(value)) ? value : parseFloat(value);

/**
 * Desktop's invoice form on a change: the edited fields (empty ones at their control's default)
 * with the change, its field mask, and the overall total of the four costs.
 */
export const applyInvoiceFormChange = (
  invoiceData: Invoice,
  name: InvoiceFormField,
  value: string | number,
): Invoice => {
  const data: Record<string, unknown> = {};
  for (const field of INVOICE_FORM_FIELDS) {
    data[field.name] = invoiceData[field.name] || (field.type === 'number' ? 0 : '');
  }
  data[name] = value;
  data.fieldMask = addToFieldMask(invoiceData.fieldMask, name);
  const total = [
    data.totalamountrow1,
    data.totalamountrow2,
    data.totalamountrow3,
    data.totalamountrow4,
  ]
    .map(Number)
    .reduce((previous, current) => previous + current);
  data.totalamounttotal = total.toString();
  return data as unknown as Invoice;
};

/**
 * What the contract editor loads, as desktop's EditContractInfo loads it: the signed-in user (for
 * the activity log), the frequencies, the customer, the contract and its invoice id, and the
 * customer's active properties.
 */
export const useEditContractQueries = ({
  userID,
  contractID,
}: {
  userID: number;
  contractID: number;
}) => {
  const auth = useAuth();
  const currentUserId = auth.user.id;
  const currentUserQuery = useUserQuery({
    filters: {
      id: currentUserId,
    },
  });
  const contractFrequenciesQuery = useContractFrequencyBatchGetQuery({
    filter: {
      isActive: 1,
    },
  });
  const customerQuery = useUserQuery({
    filters: {
      id: userID,
    },
  });
  const contractQueryEnabled = contractID !== 0;
  const contractQuery = useContractQuery({
    filter: {
      id: contractID,
    },
    enabled: contractQueryEnabled,
  });
  const invoiceQuery = useInvoiceQuery({
    filter: {
      contractId: contractID,
    },
    enabled: contractQueryEnabled,
  });
  const invoiceId = useMemo(() => {
    if (invoiceQuery.isSuccess) {
      if (invoiceQuery.data?.id) {
        return invoiceQuery.data.id;
      }
      return -1;
    }
    return -1;
  }, [invoiceQuery.data?.id, invoiceQuery.isSuccess]);
  const propertiesQuery = usePropertiesBatchGetQuery({
    filter: {
      userId: userID,
      isActive: 1,
      withoutLimit: true,
    },
  });

  return {
    currentUserId,
    currentUserQuery,
    contractFrequenciesQuery,
    customerQuery,
    contractQuery,
    invoiceQuery,
    invoiceId,
    propertiesQuery,
  };
};

/**
 * The contract's invoice as desktop's invoice form loads it (by contract and customer). A new
 * contract, or one whose invoice can't be loaded, starts from an empty invoice, as on desktop.
 */
export const useContractInvoiceData = ({
  contractID,
  userID,
  enabled = true,
}: {
  contractID: number;
  userID: number;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [queryKeys.invoice.root, 'contractEditor', contractID, userID],
    enabled,
    queryFn: async () => {
      if (contractID <= 0) return Invoice.create();
      try {
        return (
          (await InvoiceClientService.Get(
            Invoice.create({ contractId: contractID, userId: userID }),
          )) || Invoice.create()
        );
      } catch (err) {
        console.error(`An error occurred while getting an invoice: ${err}`);
        return Invoice.create();
      }
    },
  });

export type EditContractOutput = {
  contractData: Contract;
  propertiesSelected: Property[];
  invoiceData: Invoice;
};

type EditContractFormArgs = {
  userID: number;
  contractID: number;
  /** The contract as loaded, or a new contract's starting values. */
  contract: Contract;
  /** The customer's active properties. */
  properties: Property[];
  /** The contract's invoice as loaded. */
  invoice: Invoice;
  /** The invoice's id, or -1 when it has none yet (it's created on save). */
  invoiceId: number;
  currentUserId: number;
  currentUserName: { firstname?: string; lastname?: string };
  customerLoaded: boolean;
  customerEmail: string | undefined;
  onSaveFinished?: (saved: EditContractOutput) => void;
};

const sameIds = (a: Property[], b: Property[]) =>
  a.length === b.length && a.every((property) => b.some((other) => other.id === property.id));

/**
 * The contract editor's state for the phone and tablet page: desktop's form values, change rules
 * and checks, the property selection and the invoice fields, and desktop's save. What changed is
 * worked out against the contract as loaded, so undoing a change makes it unchanged again.
 */
export const useEditContractForm = ({
  userID,
  contractID,
  contract,
  properties,
  invoice,
  invoiceId,
  currentUserId,
  currentUserName,
  customerLoaded,
  customerEmail,
  onSaveFinished,
}: EditContractFormArgs) => {
  const [initialForm] = useState(() => contractFormData(contract));
  const [formData, setFormData] = useState<Contract>(initialForm);
  const [pendingSave, setPendingSave] = useState<Contract>(contract);
  const [errors, setErrors] = useState<ContractErrors>({});
  const [initialSelected] = useState(() =>
    contractID !== 0 ? orderContractProperties(contract.properties, properties).selected : [],
  );
  const [propertiesSelected, setPropertiesSelected] = useState<Property[]>(initialSelected);
  const [invoiceData, setInvoiceData] = useState<Invoice>(invoice);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();

  const orderedProperties = useMemo(
    () =>
      contractID !== 0
        ? orderContractProperties(contract.properties, properties).sorted
        : properties,
    [contract.properties, contractID, properties],
  );

  const updateContractField = useCallback(
    (name: ContractFormField, value: string | number) => {
      const next = {
        ...formData,
        [name]: value,
        fieldMask: addToFieldMask(formData.fieldMask, name),
      } as Contract;
      setErrors(checkContractChange(next, pendingSave, errors));
      const req = applyContractFormChange(next, pendingSave, userID);
      setFormData(next);
      setPendingSave(req);
    },
    [errors, formData, pendingSave, userID],
  );

  const updateInvoiceField = useCallback((name: InvoiceFormField, value: string | number) => {
    setInvoiceData((current) => applyInvoiceFormChange(current, name, value));
  }, []);

  const toggleProperty = useCallback((property: Property) => {
    setPropertiesSelected((current) => toggleSelectedProperty(current, property));
  }, []);

  const changedFields = useMemo(() => {
    const changed: string[] = [];
    for (const { name } of CONTRACT_FORM_FIELDS) {
      if (formData[name] !== initialForm[name]) changed.push(name);
    }
    for (const { name } of INVOICE_FORM_FIELDS) {
      if ((invoiceData[name] || '') != (invoice[name] || '')) changed.push(name);
    }
    if (!sameIds(propertiesSelected, initialSelected)) changed.push('properties');
    return changed;
  }, [formData, initialForm, initialSelected, invoice, invoiceData, propertiesSelected]);

  /** Desktop's checks before saving. True when the save can go ahead (to the invoice question). */
  const validate = useCallback(() => {
    const nextErrors = checkContractErrors(pendingSave);
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [pendingSave]);

  /** Desktop's save: the contract, then its invoice (and the invoice email), then done. */
  const save = useCallback(
    async ({
      sendInvoiceToCustomer,
      paymentProcessor,
    }: {
      sendInvoiceToCustomer: boolean;
      paymentProcessor: PaymentProcessor;
    }) => {
      setIsSaving(true);
      setSaveError(undefined);
      const result = await saveContractRecord({
        pendingSave,
        propertiesSelected,
        contractID,
        currentUserId,
        activityMessage: (entry) =>
          buildContractActivityMessage(entry, currentUserName.firstname, currentUserName.lastname),
      });
      if (!result.contract) {
        setSaveError(result.error);
        setIsSaving(false);
        return false;
      }
      await saveContractInvoice({
        contract: result.contract,
        invoiceData,
        invoiceId,
        customerLoaded,
        customerEmail,
        sendInvoice: true,
        sendInvoiceToCustomer,
        paymentProcessor,
        userID,
        contractID,
      });
      setIsSaving(false);
      onSaveFinished?.({ contractData: contract, propertiesSelected, invoiceData });
      return true;
    },
    [
      contract,
      contractID,
      currentUserId,
      currentUserName.firstname,
      currentUserName.lastname,
      customerEmail,
      customerLoaded,
      invoiceData,
      invoiceId,
      onSaveFinished,
      pendingSave,
      propertiesSelected,
      userID,
    ],
  );

  return {
    formData,
    pendingSave,
    errors,
    propertiesSelected,
    orderedProperties,
    invoiceData,
    isSaving,
    saveError,
    changedFields,
    isDirty: changedFields.length > 0,
    updateContractField,
    updateInvoiceField,
    toggleProperty,
    validate,
    save,
  };
};
